import { getExtension } from '@medplum/core'
import type {
  Appointment,
  HealthcareService,
  Location,
  Patient,
  Period,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  add,
  areIntervalsOverlapping,
  endOfDay,
  isWeekend,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
} from 'date-fns'
import { compact, isUndefined } from 'lodash-es'
import type { SeenMedplumClient } from '#src/client.ts'
import * as codeSystems from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import * as appointmentsLib from '#src/models/appointments/index.ts'
import { createReference, getReferenceString } from '#src/references.ts'
import * as structureDefinitions from '#src/structureDefinitions.ts'
import { sha256 } from '#src/utils.ts'
// `rrule` unable to use named imports and requires "import *" to interop w/ `tsx` for script execution & esbuild/rollup for build
// https://github.com/jkbrzt/rrule/issues/478

export type AvailableAppointmentSlot = {
  id: string
  period: Period
  practitioners: Reference<Practitioner>[]
}

export type AvailableAppointmentSlotsRequest = {
  patient: Reference<Patient>
  healthcareService: Reference<HealthcareService>
  practitioner: Reference<Practitioner>
  start: string
  end: string
  // The appointment being moved, so it does not block its own slot.
  excludeAppointmentId?: string
}

export type AppointmentConflict = {
  conflictingAppointment: Appointment
  conflictType: 'patient' | 'practitioner' | 'location'
  conflictingParticipant?: Reference<Patient | Practitioner | Location>
}

/*
Not using the `Schedule` & `Slot` resources results in heavier computation to uncover availability
However, it means there is a higher level of flexibility in how availability is determined and less Medplum FHIR resources are used
`Schedule` & `Slot` would require many resources that may not be needed, especially after the schedules planning horizon is in the past. This
could be mitigated by cleaning up unused

## Available appointment slot surfacing
- Location/Room non-appointment related unavailability tracking (maintenance, equipment/device issues, etc.)
- Standard operating hours, weekends, holidays definition?
- Automatic appointment availability slots search expansion strategy

## Requesting appointment slot
Create Appointment?status=proposed w/ Location as participant using 'create if none exists' pattern to help prevent duplication
*/

export const getAvailableAppointmentSlots = async (
  medplum: SeenMedplumClient,
  req: AvailableAppointmentSlotsRequest,
  opts?: Pick<RequestInit, 'signal'>
): Promise<appointmentsLib.AvailableAppointmentSlot[]> => {
  const now = new Date()
  let start = startOfDay(new Date(req.start))
  if (start < now) {
    start = now
  }
  const end = endOfDay(new Date(req.end))

  if (start > end) {
    return []
  }

  // find all rooms and practitioners supporting the requested appointment type
  const [healthcareService, practitionersBundle] = await Promise.all([
    // `HealthcareService` facilitates location<->service<->practitioner mapping
    // https://www.medplum.com/docs/api/fhir/resources/healthcareservice
    medplum.readReference(req.healthcareService, { signal: opts?.signal }),

    // Search practitioners where the practitioners have a matching relevant PractitionerRole for the requested healthcare service type
    // TODO: future consideration - scope the practitioner search to the requesting patient's managing organization(s)
    medplum.search(
      'Practitioner',
      {
        '_has:PractitionerRole:practitioner:service': getReferenceString(
          req.healthcareService
        ),
      },
      { signal: opts?.signal }
    ),
  ])

  const averageServiceDurationExt = getExtension(
    healthcareService,
    structureDefinitions.AVERAGE_DURATION
  )
  invariant(
    averageServiceDurationExt?.valueQuantity?.value,
    'Healthcare service must have an average duration'
  )
  const averageServiceDuration = averageServiceDurationExt.valueQuantity.value
  const serviceDurationMinutes = averageServiceDuration

  const practitioners = req.practitioner
    ? [req.practitioner]
    : practitionersBundle.entry
        ?.map((e) => e.resource)
        .filter((p) => !isUndefined(p))
        .map((p) => createReference(p))
  invariant(
    practitioners?.length,
    'No practitioners found for the healthcare service'
  )

  // TODO: Leverage `Schedule` and `Slot` to define `Practitioner` OOO & optionally `Location` 'offline periods' i.e. maintenance / servicing needs (electrical, plumbing, etc.)
  // synced via palantir integration for `Practitioner` OOO and manually entered for Location's

  const availability: appointmentsLib.AvailableAppointmentSlot[] = []

  // TODO: improve padding/windowing strategy to consider normal operating hours, weekends, holidays, breaks, preferences, etc.

  // TODO: add logging & profiling

  const appointmentsByDate: Record<string, Appointment[]> = {}

  const appointments = await getAppointments(
    medplum,
    {
      start: start.toISOString(),
      end: end.toISOString(),
      practitioners: [req.practitioner],
      patients: [req.patient],
    },
    opts
  )

  for (const appt of appointments) {
    if (req.excludeAppointmentId && appt.id === req.excludeAppointmentId) {
      continue
    }
    const apptStart = appt.start
    const apptEnd = appt.end
    if (!apptStart || !apptEnd) {
      continue
    }

    const startDate = startOfDay(apptStart)
    const endDate = startOfDay(apptEnd)

    // Iterate through each day of the appointment and ensure it's accounted for across the entire appointment period
    // This is common with out of offices that span multiple days
    for (
      let currentDate = startDate;
      currentDate <= endDate;
      currentDate = add(currentDate, { days: 1 })
    ) {
      const dateKey = currentDate.toISOString()
      appointmentsByDate[dateKey] ??= []
      appointmentsByDate[dateKey].push(appt)
    }
  }

  /*
      TODO: Ignore weekends, holidays, etc., as defined by standard operating calendar (hard-coded or defined on the `Organization`?)
      Build availability based on existing appointment set, grouping Location and Practitioners by blocks/slots

    Define appointment packing / staggering strategies / preferences
    Maybe defined at the Organization, Location, Practitioner, or HealthcareService level
    i.e.
    Appointment/1 booked 9:00-9:40 Location/1
    If a 40min appointment is requested for and only Location/1 is available, should next availability of that location be 9:40 or pushed to 10:00?
    What kind of 'padding strategies' are commonly employed in PACE facilities?

    Strategy for automatic search expansion, if not enough availability uncovered?
  */

  const patientRefString = getReferenceString(req.patient)

  const MAX_SLOTS_PER_DAY = 50
  /*
  walk from start to end date, building availability blocks based on the service duration and existing appointments
  surface at most MAX_SLOTS_PER_DAY
  only build slots during standard operating hours
  */
  for (let d = start; d < end; d = add(d, { days: 1 })) {
    if (isWeekend(d)) {
      continue
    }
    const dayAppts = appointmentsByDate[startOfDay(d).toISOString()] ?? []

    // TODO: consider using timezone offsets defined on the facility Location
    // Pull start & end time from facility Location's `hoursOfOperation`
    const dayStart = setSeconds(setMinutes(setHours(d, 9), 0), 0)
    const dayEnd = setSeconds(setMinutes(setHours(d, 17), 0), 0)

    const daysAvailability: Omit<
      appointmentsLib.AvailableAppointmentSlot,
      'id'
    >[] = []
    let slotStart = dayStart
    while (slotStart < dayEnd && daysAvailability.length < MAX_SLOTS_PER_DAY) {
      const slotEnd = add(slotStart, { minutes: serviceDurationMinutes })
      if (slotEnd > dayEnd) {
        break
      }

      if (slotStart < now) {
        slotStart = slotEnd
        continue
      }

      const patientAvailable = !dayAppts.some((appt) => {
        const apptStart = appt.start
        const apptEnd = appt.end
        if (!apptStart || !apptEnd) {
          return false
        }

        return (
          appt.participant.some(
            (p) => p.actor && getReferenceString(p.actor) === patientRefString
          ) &&
          areIntervalsOverlapping(
            { start: apptStart, end: apptEnd },
            {
              start: slotStart.toISOString(),
              end: slotEnd.toISOString(),
            }
          )
        )
      })
      if (!patientAvailable) {
        slotStart = slotEnd
        continue
      }

      const availablePractitioners = practitioners.filter(
        (practitionerRef) =>
          !dayAppts.some((appt) => {
            const practitionerRefString = getReferenceString(practitionerRef)
            const apptStart = appt.start
            const apptEnd = appt.end
            invariant(apptStart, 'Appointment must have a start time')
            invariant(apptEnd, 'Appointment must have an end time')

            return (
              appt.participant.some(
                (p) =>
                  p.actor &&
                  getReferenceString(p.actor) === practitionerRefString
              ) &&
              areIntervalsOverlapping(
                { start: apptStart, end: apptEnd },
                {
                  start: slotStart.toISOString(),
                  end: slotEnd.toISOString(),
                }
              )
            )
          })
      )

      if (availablePractitioners.length > 0) {
        daysAvailability.push({
          period: {
            start: slotStart.toISOString(),
            end: slotEnd.toISOString(),
          },
          practitioners: availablePractitioners,
        })
      }

      slotStart = slotEnd
    }

    const daysAvailabilityWithIds = await Promise.all(
      daysAvailability.map(async (e) => ({
        // sha256 id for deterministic uniqueness
        id: await sha256(JSON.stringify(e)),
        ...e,
      }))
    )

    availability.push(...daysAvailabilityWithIds)
  }

  return availability
}

export async function getAppointments(
  medplum: SeenMedplumClient,
  {
    start,
    end,
    practitioners,
    locations,
    patients,
    healthcareServices,
    statuses,
  }: {
    start: string
    end: string
    practitioners?: Reference<Practitioner>[]
    locations?: Reference<Location>[]
    patients?: Reference<Patient>[]
    healthcareServices?: Reference<HealthcareService>[]
    statuses?: Appointment['status'][]
  },
  opts?: Pick<RequestInit, 'signal'>
): Promise<Appointment[]> {
  const practitionerReferenceStrings = practitioners?.map(getReferenceString)
  const patientReferenceStrings = patients?.map(getReferenceString)
  const locationReferenceStrings = locations?.map(getReferenceString)
  const healthcareServiceReferenceStrings =
    healthcareServices?.map(getReferenceString)

  const referenceFilters = compact([
    practitionerReferenceStrings?.length &&
      `practitioner re ${practitionerReferenceStrings.join(',')}`,
    patientReferenceStrings?.length &&
      `patient re ${patientReferenceStrings.join(',')}`,
    locationReferenceStrings?.length &&
      `location re ${locationReferenceStrings.join(',')}`,
    healthcareServiceReferenceStrings?.length &&
      `actor re ${healthcareServiceReferenceStrings.join(',')}`,
  ])

  const clinicalAppointmentsReferenceFilterString = referenceFilters.length
    ? `and (${referenceFilters.join(' or ')})`
    : ''
  const filterConditions = compact([
    `(appointment-type ne ${codeSystems.APPOINTMENT_TYPE}|${codeSystems.AppointmentType.Busy} ${clinicalAppointmentsReferenceFilterString} and date le ${end} and end ge ${start})`,
  ])
  const _filter = filterConditions.join(' or ')

  const searchParams = new URLSearchParams()
  if (statuses?.length) {
    searchParams.set('status', statuses.join(','))
  }
  searchParams.set('_filter', _filter)
  // `-date` alone is not unique. Offset pages then repeat some visits and
  // skip others that share a start instant, so a day-center cohort booked at
  // one time is not all returned. A trailing id makes each page stable.
  searchParams.set('_sort', '-date,_id')
  searchParams.set('_count', '100')

  const appointments: Appointment[] = []

  for await (const page of medplum.searchResourcePages(
    'Appointment',
    searchParams.toString(),
    { signal: opts?.signal }
  )) {
    for (const appt of page) {
      const apptStart = appt.start
      const apptEnd = appt.end
      if (!apptStart || !apptEnd) {
        continue
      }

      appointments.push(appt)
    }
  }

  return appointments
}

export async function findAppointmentConflicts(
  medplum: SeenMedplumClient,
  {
    start,
    end,
    patient,
    practitioners,
    location,
    excludeAppointmentId,
  }: {
    start: string
    end: string
    patient: Reference<Patient>
    practitioners: Reference<Practitioner>[]
    location?: Reference<Location>
    excludeAppointmentId?: string
  },
  opts?: Pick<RequestInit, 'signal'>
): Promise<AppointmentConflict[]> {
  const appointments = await getAppointments(
    medplum,
    {
      start,
      end,
      practitioners,
      patients: [patient],
      locations: location ? [location] : undefined,
      statuses: ['booked'],
    },
    opts
  )

  const conflicts: AppointmentConflict[] = []

  for (const appt of appointments) {
    if (excludeAppointmentId && appt.id === excludeAppointmentId) {
      continue
    }

    const apptStart = appt.start
    const apptEnd = appt.end
    if (!apptStart || !apptEnd) {
      continue
    }

    if (
      !areIntervalsOverlapping(
        { start: apptStart, end: apptEnd },
        { start, end }
      )
    ) {
      continue
    }

    const patientConflict = appt.participant.find(
      (p) =>
        p.actor && getReferenceString(p.actor) === getReferenceString(patient)
    )
    if (patientConflict?.actor) {
      conflicts.push({
        conflictingAppointment: appt,
        conflictType: 'patient',
        conflictingParticipant: patientConflict.actor as Reference<Patient>,
      })
    }

    for (const practitioner of practitioners) {
      const practitionerConflict = appt.participant.find(
        (p) =>
          p.actor &&
          getReferenceString(p.actor) === getReferenceString(practitioner)
      )
      if (practitionerConflict?.actor) {
        conflicts.push({
          conflictingAppointment: appt,
          conflictType: 'practitioner',
          conflictingParticipant:
            practitionerConflict.actor as Reference<Practitioner>,
        })
      }
    }

    if (location) {
      const locationConflict = appt.participant.find(
        (p) =>
          p.actor &&
          getReferenceString(p.actor) === getReferenceString(location)
      )
      if (locationConflict?.actor) {
        conflicts.push({
          conflictingAppointment: appt,
          conflictType: 'location',
          conflictingParticipant: locationConflict.actor as Reference<Location>,
        })
      }
    }
  }

  return conflicts
}

export async function findConflictingAppointmentGroups(
  medplum: SeenMedplumClient,
  {
    start,
    end,
    statuses = ['booked'],
  }: {
    start: string
    end: string
    statuses?: Appointment['status'][]
  },
  opts?: Pick<RequestInit, 'signal'>
): Promise<appointmentsLib.ExtendedAppointment[][]> {
  const appointments = (
    await getAppointments(
      medplum,
      {
        start,
        end,
        statuses,
      },
      opts
    )
  )
    .map((appt) => appointmentsLib.ExtendedAppointment.fromFhirResource(appt))
    .filter((appt) => !appt.isDayCenter && !appt.isSpecialistVisit)

  const validAppointments = appointments.filter((appt) => appt.id)

  if (validAppointments.length <= 1) {
    return []
  }

  const shareParticipants = (
    appt1: appointmentsLib.ExtendedAppointment,
    appt2: appointmentsLib.ExtendedAppointment
  ): boolean => {
    const appt1Participants = appt1.personParticipants.map(getReferenceString)
    const appt2Participants = appt2.personParticipants.map(getReferenceString)

    return appt1Participants.some((p) => appt2Participants.includes(p))
  }

  const appointmentsOverlap = (
    appt1: appointmentsLib.ExtendedAppointment,
    appt2: appointmentsLib.ExtendedAppointment
  ): boolean => {
    if (!appt1.start || !appt1.end || !appt2.start || !appt2.end) {
      return false
    }
    return areIntervalsOverlapping(
      { start: appt1.start, end: appt1.end },
      { start: appt2.start, end: appt2.end }
    )
  }

  const conflictGraph = new Map<string, Set<string>>()

  validAppointments.forEach((appt) => {
    invariant(appt.id, 'Appointment must have an ID')
    conflictGraph.set(appt.id, new Set())
  })

  for (let i = 0; i < validAppointments.length; i++) {
    for (let j = i + 1; j < validAppointments.length; j++) {
      const appt1 = validAppointments[i]
      const appt2 = validAppointments[j]
      invariant(appt1.id && appt2.id, 'Appointments must have IDs')

      if (
        appointmentsOverlap(appt1, appt2) &&
        shareParticipants(appt1, appt2)
      ) {
        const appt1Conflicts = conflictGraph.get(appt1.id)
        const appt2Conflicts = conflictGraph.get(appt2.id)
        invariant(appt1Conflicts && appt2Conflicts, 'Conflict sets must exist')

        appt1Conflicts.add(appt2.id)
        appt2Conflicts.add(appt1.id)
      }
    }
  }

  const visited = new Set<string>()
  const conflictGroups: appointmentsLib.ExtendedAppointment[][] = []

  const dfs = (apptId: string, currentGroup: Set<string>): void => {
    visited.add(apptId)
    currentGroup.add(apptId)

    const neighbors = conflictGraph.get(apptId)
    invariant(neighbors, 'Neighbors set must exist')

    for (const neighborId of neighbors) {
      if (!visited.has(neighborId)) {
        dfs(neighborId, currentGroup)
      }
    }
  }

  for (const appt of validAppointments) {
    invariant(appt.id, 'Appointment must have an ID')
    if (!visited.has(appt.id)) {
      const currentGroup = new Set<string>()
      dfs(appt.id, currentGroup)

      if (currentGroup.size > 1) {
        conflictGroups.push(
          validAppointments.filter((a) => a.id && currentGroup.has(a.id))
        )
      }
    }
  }

  return conflictGroups
}
